import { createLogger } from "@risalabs_frontend_org/logger";
import {
  AuthStatusChangeComments,
  isValidAuthStatusChangeComments,
} from "../../../data-model/authStatusChangeComments";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../../store/UserGeneralInfoStore";

const log = createLogger(
  "AuthStatusChangeCommentsStore",
  "helperForAuthStatusCommentsInLocalStorage.ts",
);

/**
 * Retrieves saved authorization status change comments from the store for a specific order.
 *
 * @param orderId - The unique identifier of the order
 * @returns An array of authorization status change comments if found, otherwise null
 */
export const getSavedAuthStatusChangeCommentsFromLocalStorage = (
  orderId: string,
): AuthStatusChangeComments[] | null => {
  const key = `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments = UserStore.getItem(key);
  return comments ?? null;
};

/**
 * Removes a specific authorization status change comment from the store based on J-code.
 * Finds and removes the comment matching the provided J-code from the stored comments array.
 *
 * @param orderId - The unique identifier of the order
 * @param j_code - The J-code identifying which comment to remove
 */
export const removeAuthStatusChangesFromLocalStorageByJCode = (
  orderId: string,
  j_code: string,
) => {
  const key = `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments: AuthStatusChangeComments[] | null = UserStore.getItem(key);

  if (comments && comments !== null) {
    const index = comments.findIndex((comment) => comment.j_code === j_code);
    if (index !== -1) {
      comments.splice(index, 1);
      UserStore.setItem(key, comments);
    }
  }
};

/**
 * Adds a new authorization status change comment to the store for a specific order.
 * If comments already exist and are valid, appends the new comment; otherwise
 * creates a new array. Logs each insert via the AuthStatusChangeCommentsStore logger.
 *
 * @input orderId - The unique identifier of the order
 * @input comment - The authorization status change comment to add
 * @return void
 */
export const pushAuthStatusChangeCommentsToLocalStorage = (
  orderId: string,
  comment: AuthStatusChangeComments,
) => {
  const key = `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments: AuthStatusChangeComments[] | null = UserStore.getItem(key);
  if (
    comments &&
    comments !== null &&
    isValidAuthStatusChangeComments(comments ?? [])
  ) {
    comments.push(comment);
    UserStore.setItem(key, comments ?? []);
    log.info("Appended auth status change comment", {
      orderId,
      comment,
      totalComments: comments.length,
    });
  } else {
    UserStore.setItem(key, [comment]);
    log.info("Inserted first auth status change comment", {
      orderId,
      comment,
    });
  }
};

/**
 * Removes all authorization status change comments from the store for a specific order.
 *
 * @param orderId - The unique identifier of the order
 */
export const clearAuthStatusChangeCommentsFromLocalStorage = (
  orderId: string,
) => {
  const key = `${UserGeneralInfoContextKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  UserStore.removeItem(key);
};
